import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import { objectToSnake } from 'ts-case-convert';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { Observable } from 'rxjs';
import { APP } from 'src/app/config/app';
import { IBase, IVehiclePayload, IVehicleScheduleFilters, IVehicleScheduleUpdate } from 'src/app/models';
import { HttpResponseType, ScheduleGroupBy } from 'src/app/models/enums';
import { serializeStringList } from 'src/app/filters';

@Injectable({
  providedIn: 'root',
})
export class VehiclesHttpService extends BaseHttpService {
  private baseURL = `vehicles`;

  constructor(
    @Inject(HttpClient) httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public getSetupVehicles(routeId: number) {
    const path = `/service/${routeId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getVehicleSchedule(
    page: number,
    size: number,
    from?: Date,
    to?: Date,
    groupBy?: ScheduleGroupBy,
    filters?: IVehicleScheduleFilters,
  ) {
    let path = `/schedule?page=${page}&size=${size}`;
    // the backend reads them as LocalDateTime, so they travel without a timezone
    if (from) path += `&from=${this.localDateTime(from)}`;
    if (to) path += `&to=${this.localDateTime(to)}`;
    if (groupBy) path += `&group_by=${groupBy}`;
    if (filters) {
      path += this.idsParam('clients', filters.clients);
      path += this.idsParam('operators', filters.operators);
      path += this.valuesParam('types', filters.types);
      path += this.valuesParam('serviceTypes', filters.serviceTypes);
      path += this.idsParam('towns', filters.zones);
      path += this.idsParam('services', filters.services);
      path += this.idsParam('routes', filters.routes);
      path += this.idsParam('vehicles', filters.vehicles);
      path += this.idsParam('drivers', filters.drivers);
    }
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }
  
  public filterVehicles(
    page: number = 1,
    size: number = 10,
    clientsSelected: number[],
    operatorsSelected: number[],
    active?: boolean,
    searchValue?: string
  ) {
    let path = `/filter?page=${page}&size=${size}`;
    if (clientsSelected && clientsSelected.length > 0) path += `&clients=${clientsSelected.join(',')}`;
    if (operatorsSelected && operatorsSelected.length > 0) path += `&operators=${operatorsSelected.join(',')}`;
    if (active !== undefined) path += `&active=${active}`;
    if (searchValue) path += `&search=${encodeURIComponent(searchValue)}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getVehicles(clientId?: number) {
    const path = clientId != null ? `?client_id=${clientId}` : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getVehicle(vehicleId: number) {
    const path = `/${vehicleId}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public createVehicle(payload: IVehiclePayload) {
    const url = this.routerService.mount(`${this.baseURL}`);
    return this.create(objectToSnake(payload), url);
  }

  public updateVehicle(vehicleId: number, payload: IVehiclePayload) {
    const url = this.routerService.mount(`${this.baseURL}/${vehicleId}`);
    return this.update(objectToSnake(payload), url);
  }

  public applySchedule(update: IVehicleScheduleUpdate): Observable<string> {
    const body = objectToSnake({
      sendNotification: update.sendNotification,
      availabilities: update.availabilities.map((availability) => ({
        ...availability,
        startDateTime: this.localDateTime(availability.startDateTime),
        endDateTime: this.localDateTime(availability.endDateTime),
        trips: availability.trips.map((trip) => ({ ...trip, startDateTime: this.localDateTime(trip.startDateTime) })),
      })),
    });
    const url = this.routerService.mount(`${this.baseURL}/schedule`);
    return this.update(body, url, HttpResponseType.TEXT) as Observable<string>;
  }

  private localDateTime(date?: Date): string | undefined {
    return date ? moment(date).format(APP.SCHEDULE_LOCAL_DATE_TIME_FORMAT) : undefined;
  }

  private idsParam(name: string, items: IBase[]): string {
    return items.length ? `&${name}=${items.map((item) => item.id).join(',')}` : '';
  }

  private valuesParam(name: string, values: string[]): string {
    const serialized = serializeStringList(values);
    return serialized ? `&${name}=${serialized}` : '';
  }
}
